import { MenuItem, ListItemIcon, Box, Stack } from '@mui/material';
import { IconButton, Typography, colors } from '@sweep-io/sweep-design';
import { CustomWebhookInterface, useWebhooksContext } from '../webhooks/WebhooksContext';
import { ActionsMenu } from '../../common/actions-menu/ActionsMenu';
import { Delete, Edit, Integrations } from '@sweep-io/sweep-design/dist/icons';
import { LogoDevIcon } from '../../common/logo-dev-selector/LogoDevIcon';
import { useIntegrations } from '../../../hooks/useIntegrations';
import useConfirm from '../../common/dialogs/ConfirmLeaveWithoutSave/useConfirm';
import ConfirmDestructiveDialog from '../../common/dialogs/ConfirmDestructiveDialog';
import { useState } from 'react';
import { UsedIntegrationDialog } from './UsedIntegrationDialog';
import { usePermissions } from '../../../permissions/usePermissions';
import RestrictedTooltip from '../../common/permissions/RestrictedTooltip';
import { AuditDialog } from '../../common/audit-table/AuditDialog';
import { AuditReportType } from '../../common/audit-table/AuditTable';

interface CustomIntegrationOptionProps {
  integration: CustomWebhookInterface;
}
enum WebhookActionsEnum {
  DELETE = 'DELETE',
  ACTIVITY = 'ACTIVITY',
}

export const CustomIntegrationsOption = ({ integration }: CustomIntegrationOptionProps) => {
  const { deleteCustomIntegration } = useIntegrations();
  const { setWebhooks, setEditWebhook } = useWebhooksContext();
  const { openConfirm, onCancel, onConfirm } = useConfirm();
  const [usedAutomationList, setUsedAutomationList] = useState([]);
  const [isUsedIntegrationOpen, setIsUsedIntegrationOpen] = useState(false);
  const [isActivityOpen, setIsActivityOpen] = useState(false);
  const { evaluatePermissions: checkPermissions } = usePermissions();
  const [isAllowedBtnEdit, isAllowedBtnDelete] = checkPermissions([
    { permission: 'edit:data-integration:custom-integrations' },
    { permission: 'delete:data-integration:custom-integrations' },
  ]);

  const menuActions = [
    {
      label: 'Integration activity',
      value: WebhookActionsEnum.ACTIVITY,
      actionIcon: <Integrations size="medium" />,
      disabled: false,
    },
    {
      label: 'Delete',
      value: WebhookActionsEnum.DELETE,
      actionIcon: <Delete size="medium" color={colors.blush[600]} />,
      disabled: !isAllowedBtnDelete,
      disabledTooltip: 'To adjust integration settings, please contact your admin.',
      actionSx: { color: colors.blush[600] },
    },
  ];

  const handleDelete = async (isDryRun: boolean) => {
    try {
      await deleteCustomIntegration(integration?.id, isDryRun);
      //if ok show confirm dialog and resend with no dry-run
      if (isDryRun) {
        const isConfirmed = await openConfirm(
          <ConfirmDestructiveDialog
            open={true}
            onConfirm={onConfirm}
            onClose={onCancel}
            title={`Delete ${integration?.name} integration from Sweep?`}
            confirmText="Delete"
            cancelText="Cancel"
          />,
        );
        if (isConfirmed) {
          handleDelete(false);
        }
      }
    } catch (error: any) {
      //show the used automations list
      const usedAutomationList = error?.response?.data?.automationIds;
      if (usedAutomationList?.length > 0) {
        setUsedAutomationList(usedAutomationList);
        setIsUsedIntegrationOpen(true);
      }
    }
  };

  return (
    <>
      <MenuItem>
        <Stack width="100%">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {integration?.logo ? (
                <Box
                  sx={{
                    width: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <LogoDevIcon brand={integration.logo} size={24} />
                </Box>
              ) : (
                <ListItemIcon>
                  <Box
                    sx={{
                      background: colors.blue[100],
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      textAlign: 'center',
                      lineHeight: '24px',
                    }}
                  >
                    <Integrations size="small" color={colors.blue[600]} />
                  </Box>
                </ListItemIcon>
              )}

              <Box
                sx={{
                  flex: '1',
                  textWrap: 'wrap',
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 0,
                }}
              >
                <Typography variant="body-medium" color={colors.grey[900]}>
                  {integration.name}
                </Typography>
                <Typography variant="body" color={colors.grey[700]}>
                  Custom integration
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <RestrictedTooltip
                validation={{ type: 'explicit', allowed: isAllowedBtnEdit }}
                notAllowedTitle={'To adjust integration settings, please contact your admin.'}
              >
                <IconButton
                  onClick={() => {
                    setEditWebhook(integration);
                    setWebhooks(true);
                  }}
                  variant="flat"
                  size="tiny"
                  disabled={!isAllowedBtnEdit}
                >
                  <Edit size="medium" />
                </IconButton>
              </RestrictedTooltip>
              <ActionsMenu
                iconSize="tiny"
                actions={menuActions}
                onClick={(action) => {
                  if (action?.value === WebhookActionsEnum.DELETE) {
                    handleDelete(true);
                  }
                  if (action?.value === WebhookActionsEnum.ACTIVITY) {
                    setIsActivityOpen(true);
                  }
                }}
                iconVariant="system"
                menuWidth="220px"
              />
            </Box>
          </Box>
        </Stack>
      </MenuItem>
      {isUsedIntegrationOpen && (
        <UsedIntegrationDialog
          usedAutomationList={usedAutomationList}
          integration={integration}
          isOpen={isUsedIntegrationOpen}
          onClose={() => {
            setIsUsedIntegrationOpen(false);
            setUsedAutomationList([]);
          }}
        />
      )}
      {isActivityOpen && (
        <AuditDialog
          onClose={() => setIsActivityOpen(false)}
          auditType={AuditReportType.INTEGRATION_WEBHOOKS}
          integrationId={integration?.id}
        />
      )}
    </>
  );
};
